// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import { Button, Group, Input, Stack, TextInput } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import { normalizeErrorString } from '@medplum/core';
import type { Practitioner } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { isNpi10, isNpiLuhnValid, isUsPhone10 } from '@polarissleep/shared';
import { IconCheck } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { type JSX, useState } from 'react';
import { IMaskInput } from 'react-imask';

import { FormCard } from '../../../components/FormCard';
import {
  DEA_SYSTEM,
  getIdentifier,
  getQualificationText,
  getTelecom,
  NPI_SYSTEM,
  PTAN_SYSTEM,
  QUALIFICATION_TYPE_SYSTEM,
} from '../../../utils/practitioner';

interface ProviderInformationSectionProps {
  practitioner: Practitioner;
  onSaved: (updated: Practitioner) => void;
  isEditing: boolean;
  onStartEdit: () => void;
  onEndEdit: () => void;
}

export function ProviderInformationSection({
  practitioner,
  onSaved,
  isEditing,
  onStartEdit,
  onEndEdit,
}: ProviderInformationSectionProps): JSX.Element {
  const medplum = useMedplum();
  const [saving, setSaving] = useState(false);

  const form = useForm({
    initialValues: {
      title: practitioner.name?.[0]?.prefix?.[0] ?? '',
      firstName: practitioner.name?.[0]?.given?.[0] ?? '',
      lastName: practitioner.name?.[0]?.family ?? '',
      phone: getTelecom(practitioner, 'phone'),
      npi: getIdentifier(practitioner, NPI_SYSTEM),
      degree: getQualificationText(practitioner.qualification ?? [], 'degree'),
      certification: getQualificationText(practitioner.qualification ?? [], 'certification'),
      dea: getIdentifier(practitioner, DEA_SYSTEM),
      ptan: getIdentifier(practitioner, PTAN_SYSTEM),
      taxonomy: getQualificationText(practitioner.qualification ?? [], 'taxonomy'),
      birthDate: practitioner.birthDate ? new Date(practitioner.birthDate) : null,
    },
    validate: {
      title: (v: string) => (v.trim() ? null : 'Title is required'),
      firstName: (v: string) => (v.trim() ? null : 'First name is required'),
      lastName: (v: string) => (v.trim() ? null : 'Last name is required'),
      phone: (v: string) => {
        if (!v) {
          return 'Phone is required';
        }
        if (isUsPhone10(v)) {
          return null;
        }
        return 'Enter a valid 10-digit US phone number';
      },
      npi: (v: string) => {
        const trimmed = v.trim();
        if (!trimmed) {
          return 'NPI is required';
        }
        if (!isNpi10(trimmed)) {
          return 'NPI must be exactly 10 digits';
        }
        if (!isNpiLuhnValid(trimmed)) {
          return 'NPI checksum is invalid';
        }
        return null;
      },
      degree: (v: string) => (v.trim() ? null : 'Degree type is required'),
      certification: (v: string) => (v.trim() ? null : 'Certification is required'),
      dea: (v: string) => (v.trim() ? null : 'DEA number is required'),
      birthDate: (v: Date | null) => (v ? null : 'Date of birth is required'),
    },
  });

  const handleSave = form.onSubmit(async (values: typeof form.values) => {
    setSaving(true);
    try {
      const updated: Practitioner = {
        ...practitioner,
        birthDate: values.birthDate ? dayjs(values.birthDate).format('YYYY-MM-DD') : undefined,
        name: [
          {
            ...practitioner.name?.[0],
            prefix: values.title ? [values.title] : undefined,
            given: values.firstName ? [values.firstName] : undefined,
            family: values.lastName || undefined,
          },
        ],
        telecom: [
          ...(practitioner.telecom?.filter((t) => t.system !== 'phone') ?? []),
          ...(values.phone ? [{ system: 'phone' as const, value: values.phone }] : []),
        ],
        identifier: [
          ...(practitioner.identifier?.filter(
            (id) => id.system !== NPI_SYSTEM && id.system !== DEA_SYSTEM && id.system !== PTAN_SYSTEM
          ) ?? []),
          ...(values.npi ? [{ system: NPI_SYSTEM, value: values.npi }] : []),
          ...(values.dea ? [{ system: DEA_SYSTEM, value: values.dea }] : []),
          ...(values.ptan ? [{ system: PTAN_SYSTEM, value: values.ptan }] : []),
        ],
        qualification: [
          ...(practitioner.qualification?.filter((q) => q.code?.coding?.[0]?.system !== QUALIFICATION_TYPE_SYSTEM) ??
            []),
          ...(values.degree
            ? [{ code: { coding: [{ system: QUALIFICATION_TYPE_SYSTEM, code: 'degree' }], text: values.degree } }]
            : []),
          ...(values.certification
            ? [
                {
                  code: {
                    coding: [{ system: QUALIFICATION_TYPE_SYSTEM, code: 'certification' }],
                    text: values.certification,
                  },
                },
              ]
            : []),
          ...(values.taxonomy
            ? [
                {
                  code: {
                    coding: [{ system: QUALIFICATION_TYPE_SYSTEM, code: 'taxonomy' }],
                    text: values.taxonomy,
                  },
                },
              ]
            : []),
        ],
        extension: [...(practitioner.extension ?? [])],
      };
      const result = await medplum.updateResource(updated);
      onSaved(result);
      onEndEdit();
      showNotification({
        color: 'green',
        icon: <IconCheck size={16} />,
        message: 'Provider information updated',
      });
    } catch (err) {
      showNotification({ color: 'red', message: normalizeErrorString(err), autoClose: false });
    } finally {
      setSaving(false);
    }
  });

  return (
    <FormCard
      title="Provider Information"
      isEditing={isEditing}
      action={
        isEditing ? (
          <Group gap="xs">
            <Button
              size="xs"
              variant="default"
              onClick={() => {
                form.reset();
                onEndEdit();
              }}
            >
              Cancel
            </Button>
            <Button size="xs" onClick={() => handleSave()} loading={saving}>
              Save
            </Button>
          </Group>
        ) : (
          <Button size="xs" onClick={onStartEdit}>
            Edit
          </Button>
        )
      }
    >
      <Stack gap="sm">
        <Group grow>
          <TextInput
            label="Title"
            placeholder="e.g. Dr., PA-C"
            withAsterisk
            disabled={!isEditing}
            {...form.getInputProps('title')}
          />
          <Group grow>
            <TextInput
              label="First name"
              placeholder="First name"
              withAsterisk
              disabled={!isEditing}
              {...form.getInputProps('firstName')}
            />
            <TextInput
              label="Last name"
              placeholder="Last name"
              withAsterisk
              disabled={!isEditing}
              {...form.getInputProps('lastName')}
            />
          </Group>
        </Group>
        <Group grow>
          <TextInput label="Email" value={getTelecom(practitioner, 'email')} readOnly disabled />
          <Input.Wrapper label="Phone" withAsterisk {...form.getInputProps('phone')}>
            <Input
              component={IMaskInput}
              mask="+1 (000) 000-0000"
              placeholder="+1 (000) 000-0000"
              disabled={!isEditing}
              {...form.getInputProps('phone')}
            />
          </Input.Wrapper>
        </Group>
        <Group grow>
          <TextInput
            label="NPI"
            placeholder="10-digit NPI number"
            withAsterisk
            disabled={!isEditing}
            {...form.getInputProps('npi')}
          />
          <TextInput
            label="Degree type(s)"
            placeholder="e.g. MD, DO, PA"
            withAsterisk
            disabled={!isEditing}
            {...form.getInputProps('degree')}
          />
        </Group>
        <Group grow>
          <TextInput
            label="Certification(s)"
            placeholder="e.g. ABSM, RPSGT"
            withAsterisk
            disabled={!isEditing}
            {...form.getInputProps('certification')}
          />
          <TextInput
            label="DEA number"
            placeholder="AA0000000"
            withAsterisk
            disabled={!isEditing}
            {...form.getInputProps('dea')}
          />
        </Group>
        <Group grow>
          <TextInput label="PTAN" placeholder="PTAN number" disabled={!isEditing} {...form.getInputProps('ptan')} />
          <TextInput
            label="Taxonomy"
            placeholder="e.g. 207RS0012X"
            disabled={!isEditing}
            {...form.getInputProps('taxonomy')}
          />
        </Group>
        <DatePickerInput
          label="Date of birth"
          placeholder="MM/DD/YYYY"
          maxDate={new Date()}
          clearable
          withAsterisk
          disabled={!isEditing}
          {...form.getInputProps('birthDate')}
        />
      </Stack>
    </FormCard>
  );
}
